import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'
import { Column } from 'react-table'
import { Trans, t } from '@lingui/macro'

import {
  TrackedLinkAsStudent,
  UserLinkClick
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  formatDateAndTimeShort,
  formatDateShort
} from 'front-end/src/utils/dateHelpers'
import TableExtendedOld from 'front-end/src/components/common/table-extended-old/TableExtendedOld'

import { ManageCampaignContext } from '../ManageCampaignPage'
import UserDetail from '../../../components/common/user/user-detail/UserDetail'
import ResettableInput from '../../../components/common/form/input/ResettableInput'
import { useDebouncedState } from '../../../app/hooks/useDebouncedState'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import {
  ICampaignLinkReportsFilters,
  SelectedActions
} from './CampaignLinkReport'

import IconLink from '../../../components/common/icon/IconLink'
import TablePagination from '../../../components/common/table-extended-old/table-extended-pagination/TablePagination'
import Tag from '../../../components/common/tag/Tag'
import { useCampaignTrackedLinkReportsQuery } from './CampaignLinkReportOverviewTable.codegen'
import CampaignLinkReportDownloadCSV from './CampaignLinkReportDownloadCSV'
import { NbSp } from '../../../utils/NbSp'
import { Center } from 'front-end/src/components'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export enum LinkClickReportTypeEnum {
  NOT_CLICKED = 'not-clicked',
  CLICKED = 'clicked'
}

export interface ISelectedActions {
  campaignEmailActionIds: number[]
  campaignMessageActionIds: number[]
  campaignPathwayShareActionIds: number[]
  campaignCertificationShareActionIds: number[]
  campaignTopicShareActionIds: number[]
  campaignSurveyShareActionIds: number[]
}

interface IReportProps {
  onFilterChange: (filters: Partial<ICampaignLinkReportsFilters>) => void
  filters: ICampaignLinkReportsFilters
}

export const protocolRegexp = /(^\w+:|^)\/\//
export const trailingSlashRegexp = /\/$/

const CampaignLinkReportOverviewTable: FC<IReportProps> = ({
  onFilterChange,
  filters
}) => {
  const limit = 20
  const { campaign } = useContext(ManageCampaignContext)
  const selectedActions = prepareSelectedActionsFilter(filters.selectedActions)
  const { data } = useCampaignTrackedLinkReportsQuery({
    variables: {
      campaignId: campaign.id,
      trackedLinkReportFilters: {
        displayName: filters.search.length > 0 ? filters.search : null,
        page: filters.page,
        limit,
        ...selectedActions
      },
      trackedLinksFilters: selectedActions
    }
  })

  const trackedLinks = data?.admin?.trackedLinks
  const report = data?.admin?.campaign?.trackedLinksReport
  const [search, setSearch] = useState<string>(filters.search || '')
  const debouncedSearchTerm = useDebouncedState(search, 250)

  useEffect(() => {
    onFilterChange({ search })
  }, [debouncedSearchTerm])

  const renderHeaderActionColumn = (trackedLink: TrackedLinkAsStudent) => {
    const tooltipContent = (
      <>
        <div className="EventTooltip">
          <Tag
            color={trackedLink.removed_from_source_at ? 'greyLight' : 'blue'}
          >
            <IconLink />{' '}
            {trackedLink.removed_from_source_at ? (
              <Trans>Inactive link</Trans>
            ) : (
              <Trans>Link</Trans>
            )}
          </Tag>
        </div>
        <div className="EventTooltip">
          <span className="EventTooltip__name">URL:</span>
          <NbSp />
          <span className="EventTooltip__wordbreak">{trackedLink.link}</span>
        </div>
        <div className="EventTooltip">
          <span className="EventTooltip__name">
            <Trans>Added to campaign</Trans>:
          </span>
          <NbSp />
          {formatDateShort(trackedLink.created_at as string)}
        </div>
        {trackedLink.removed_from_source_at && (
          <div className="EventTooltip">
            <span className="EventTooltip__name">
              <Trans>Removed</Trans>:
            </span>
            <NbSp />
            {formatDateShort(trackedLink.removed_from_source_at)}
          </div>
        )}
      </>
    )

    return (
      <header className="EventHeader">
        <Tooltip
          text={tooltipContent}
          contentClassName="TooltipContent__LinkReports"
        >
          <div
            className={`EventTooltip EventTooltip__link EventTooltip__link--${
              trackedLink.removed_from_source_at ? 'removed' : 'active'
            }`}
          >
            <IconLink />
          </div>
        </Tooltip>

        <div className="EventHeader__name">
          {(trackedLink.link as string)
            .replace(protocolRegexp, '')
            .replace(trailingSlashRegexp, '')}
        </div>
      </header>
    )
  }

  const createTableLinkColumns = (): Array<Column<any>> => {
    if (!trackedLinks) {
      return []
    }
    const tableColumns: Array<Column<any>> = trackedLinks.map((trackedLink) => {
      return {
        Header: renderHeaderActionColumn(trackedLink),
        sortable: false,
        width: 72,
        className: 'UserClickEvent',
        Cell: (row) => {
          return renderUserClickReport(trackedLink, row.original.linkClicks)
        }
      }
    })

    return tableColumns
  }

  const tableLinkColumns = createTableLinkColumns()
  const tableColumns: Array<Column<any>> = [
    {
      Header: (
        <ResettableInput
          onChange={setSearch}
          placeholder={t`Search`}
          title={t`Search`}
          showResetButton={!!search}
          showSearchIcon
          light
          value={search}
          onReset={() => {
            onFilterChange({ search: '' })
          }}
        />
      ),
      width: 200,
      sortable: false,
      headerClassName: 'FixedColumn FixedColumn__EventUserName',
      className: 'FixedColumn FixedColumn__EventUserName',
      accessor: 'user.display_name',
      Cell: (row) => {
        return (
          <UserDetail
            display_name={row.original.user.display_name}
            imageUrl={row.original.user.image_url}
          />
        )
      }
    },
    ...tableLinkColumns
  ]

  if (!trackedLinks || trackedLinks.length === 0) {
    return (
      <Center p="20px">
        <Trans>No data</Trans>
      </Center>
    )
  }

  return (
    <TableExtendedOld
      alwaysShowHeader={true}
      manual
      minRows={1}
      columns={tableColumns}
      resizable={false}
      page={filters.page}
      pages={report ? Math.ceil((report.usersCount || 1) / limit) : 1}
      onPageChange={(page) => {
        onFilterChange({ page: page })
      }}
      defaultSortDesc={true}
      data={report ? report.users : []}
      PaginationComponent={(props) => (
        <TablePagination
          {...props}
          paginationContentAfter={
            trackedLinks ? (
              <CampaignLinkReportDownloadCSV
                filters={filters}
                itemsCount={report?.usersCount || 0}
                trackedLinks={trackedLinks}
                selectedActions={selectedActions}
              />
            ) : null
          }
        />
      )}
    />
  )
}

const renderUserClickReport = (
  trackedLink: TrackedLinkAsStudent,
  allUserClicks: Array<UserLinkClick>
) => {
  // Find report for link in given column
  const clickReport = allUserClicks.find(
    (userClick) => userClick.tracked_link_id === trackedLink.id
  )

  let reportType = LinkClickReportTypeEnum.NOT_CLICKED
  let html: ReactNode = null

  if (clickReport) {
    reportType = LinkClickReportTypeEnum.CLICKED

    if (clickReport.first_click_at !== clickReport.last_click_at) {
      html = (
        <>
          <div className="EventTooltip">
            <span className="EventTooltip__name">
              <Trans>First clicked</Trans>:
            </span>
            <NbSp />
            {formatDateAndTimeShort(clickReport.first_click_at)}
          </div>
          <div className="EventTooltip">
            <span className="EventTooltip__name">
              <Trans>Last clicked</Trans>:
            </span>
            <NbSp />
            {formatDateAndTimeShort(clickReport.last_click_at)}
          </div>
          <div className="EventTooltip">
            <span className="EventTooltip__name">
              <Trans>Clicks count</Trans>:
            </span>
            <NbSp />
            {clickReport.total_clicks_count}
          </div>
        </>
      )
    } else {
      html = (
        <>
          <div className="EventTooltip">
            <span className="EventTooltip__name">
              <Trans>Clicked</Trans>:
            </span>
            <NbSp />
            {formatDateAndTimeShort(clickReport.first_click_at)}
          </div>
        </>
      )
    }
  }

  return (
    <Tooltip
      className={`CampaignReports__event CampaignReports__event--${reportType}`}
      text={html}
    >
      <NbSp />
    </Tooltip>
  )
}

const prepareSelectedActionsFilter = (
  selectedActions: SelectedActions
): ISelectedActions => {
  return {
    campaignEmailActionIds: selectedActions
      .filter(
        (selectedAction) => selectedAction.type === CampaignActionTypeEnum.EMAIL
      )
      .map((selectedAction) => selectedAction.id),
    campaignMessageActionIds: selectedActions
      .filter(
        (selectedAction) =>
          selectedAction.type === CampaignActionTypeEnum.MESSAGE
      )
      .map((selectedAction) => selectedAction.id),
    campaignPathwayShareActionIds: selectedActions
      .filter(
        (selectedAction) =>
          selectedAction.type === CampaignActionTypeEnum.PATHWAY_SHARE_ACTION
      )
      .map((selectedAction) => selectedAction.id),
    campaignCertificationShareActionIds: selectedActions
      .filter(
        (selectedAction) =>
          selectedAction.type ===
          CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION
      )
      .map((selectedAction) => selectedAction.id),
    campaignTopicShareActionIds: selectedActions
      .filter(
        (selectedAction) =>
          selectedAction.type === CampaignActionTypeEnum.TOPIC_SHARE_ACTION
      )
      .map((selectedAction) => selectedAction.id),
    campaignSurveyShareActionIds: selectedActions
      .filter(
        (selectedAction) =>
          selectedAction.type === CampaignActionTypeEnum.SURVEY_SHARE_ACTION
      )
      .map((selectedAction) => selectedAction.id)
  }
}

export default CampaignLinkReportOverviewTable
